import { useMutation } from '@apollo/client/react';
import { Alert, Badge, Button, Group, List, Modal, Stack, Text } from '@mantine/core';
import { Dropzone, type FileRejection, type FileWithPath } from '@mantine/dropzone';
import { IconAlertTriangle, IconFileUpload, IconUpload, IconX } from '@tabler/icons-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { toApiError } from '../apollo/errors';
import type {
  AdminCatalogImportInput,
  AdminCatalogImportRowInput,
  AdminImportCatalogMutation,
  CatalogCollection,
  CharacterCategory,
  CharacterType,
} from '../gql/graphql';
import { AdminCatalogDocument, AdminImportCatalogDocument } from '../graphql/operations';
import { COLLECTIONS, useLabels } from '../labels';

type Report = AdminImportCatalogMutation['adminImportCatalog'];

/**
 * A row exactly as `AdminCatalogImportRowInput` wants it. Parsed from JSON
 * rather than typed against it -- the file comes from disk, so nothing about
 * its shape is trusted until every field the mutation reads has been checked.
 */
type ImportRow = {
  id?: string;
  labels: { ru: string; en?: string };
  category?: string;
  characterType?: string;
  artworkUrl?: string;
  description?: string;
  promptDescription?: string;
  sortOrder?: number;
};

const JSON_MIME_TYPES = ['application/json', '.json'];

/**
 * Turns whatever `JSON.parse` produced into the five lists the mutation
 * takes, or throws the i18n key of why it would not. Only the top-level shape
 * is checked here -- a row with a missing name or a category only characters
 * carry is the server's rule to enforce, and it reports exactly which row and
 * why through `AdminCatalogImportRowError`.
 */
function parseInput(value: unknown): AdminCatalogImportInput {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error('catalog.import.invalidShape');
  }

  const record = value as Record<string, unknown>;
  const unknownKeys = Object.keys(record).filter(
    (key) => !COLLECTIONS.includes(key as CatalogCollection),
  );

  if (unknownKeys.length > 0) throw new Error('catalog.import.invalidShape');

  const input: AdminCatalogImportInput = {};

  for (const collection of COLLECTIONS) {
    const rows = record[collection];

    if (rows === undefined) continue;

    if (!Array.isArray(rows) || !rows.every(isImportRow)) {
      throw new Error('catalog.import.invalidShape');
    }

    input[collection] = rows.map(toRowInput);
  }

  return input;
}

function isImportRow(value: unknown): value is ImportRow {
  if (!value || typeof value !== 'object') return false;

  const row = value as Record<string, unknown>;

  if (!row.labels || typeof row.labels !== 'object') return false;

  const labels = row.labels as Record<string, unknown>;

  return typeof labels.ru === 'string';
}

/**
 * `category` and `characterType` are cast rather than checked against `CHARACTER_CATEGORIES`/`CHARACTER_TYPES`: a
 * value the enum does not have is exactly what the mutation reports back as a
 * per-row error, and duplicating that rule here would just be a second place
 * for it to go stale.
 */
function toRowInput(row: ImportRow): AdminCatalogImportRowInput {
  return {
    id: row.id ?? null,
    labels: { ru: row.labels.ru, en: row.labels.en ?? null },
    category: (row.category ?? null) as CharacterCategory | null,
    characterType: (row.characterType ?? null) as CharacterType | null,
    artworkUrl: row.artworkUrl ?? null,
    description: row.description ?? null,
    promptDescription: row.promptDescription ?? null,
    sortOrder: row.sortOrder ?? null,
  };
}

/** How many rows a parsed input carries, per collection -- shown before upload. */
function counts(input: AdminCatalogImportInput): Partial<Record<CatalogCollection, number>> {
  const result: Partial<Record<CatalogCollection, number>> = {};

  for (const collection of COLLECTIONS) {
    const rows = input[collection];

    if (rows && rows.length > 0) result[collection] = rows.length;
  }

  return result;
}

/**
 * Bulk-loads the catalogue from one JSON file covering all five tables at
 * once, by `adminImportCatalog`. Unlike `CatalogItemModal` this is not scoped
 * to the tab the operator happens to be on -- a content editor filling in
 * descriptions works from one export, not five.
 */
export function CatalogImportModal({ opened, onClose }: { opened: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const labels = useLabels();
  const [pending, setPending] = useState<AdminCatalogImportInput | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [report, setReport] = useState<Report | null>(null);

  const [runImport, { loading, error }] = useMutation(AdminImportCatalogDocument, {
    refetchQueries: COLLECTIONS.map((collection) => ({
      query: AdminCatalogDocument,
      variables: { collection },
    })),
    awaitRefetchQueries: true,
  });

  function reset() {
    setPending(null);
    setParseError(null);
    setReport(null);
  }

  function close() {
    reset();
    onClose();
  }

  async function read(file: FileWithPath) {
    setParseError(null);
    setReport(null);

    let parsed: unknown;

    try {
      parsed = JSON.parse(await file.text());
    } catch {
      setParseError(t('catalog.import.invalidJson'));

      return;
    }

    try {
      setPending(parseInput(parsed));
    } catch (thrown) {
      setParseError(t((thrown as Error).message));
    }
  }

  function reject(rejections: FileRejection[]) {
    setReport(null);
    setParseError(
      rejections[0]?.errors[0]?.code === 'file-invalid-type'
        ? t('catalog.import.invalidJson')
        : null,
    );
  }

  async function submit() {
    if (!pending) return;

    try {
      const { data } = await runImport({ variables: { input: pending } });

      if (data) {
        setReport(data.adminImportCatalog);
        setPending(null);
      }
    } catch {
      // The mutation's own error surfaces below through `error`/`toApiError`.
    }
  }

  const apiError = toApiError(error);
  const rowCounts = pending ? counts(pending) : {};

  return (
    <Modal opened={opened} onClose={close} title={t('catalog.import.title')} centered>
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          {t('catalog.import.hint')}
        </Text>

        {apiError && (
          <Alert color="red" icon={<IconAlertTriangle size={18} />}>
            {apiError.message}
          </Alert>
        )}

        {parseError && (
          <Alert color="red" icon={<IconAlertTriangle size={18} />}>
            {parseError}
          </Alert>
        )}

        {report && <ImportReportView report={report} />}

        <Dropzone
          onDrop={(files) => {
            const file = files[0];

            if (file) void read(file);
          }}
          onReject={reject}
          accept={JSON_MIME_TYPES}
          maxFiles={1}
          multiple={false}
          loading={loading}
          radius="md"
          inputProps={{ 'aria-label': t('catalog.import.dropzone') }}
        >
          <Group gap="md" wrap="nowrap" mih={88} align="center" style={{ pointerEvents: 'none' }}>
            <Dropzone.Accept>
              <IconUpload size={36} stroke={1.5} />
            </Dropzone.Accept>
            <Dropzone.Reject>
              <IconX size={36} stroke={1.5} />
            </Dropzone.Reject>
            <Dropzone.Idle>
              <IconFileUpload size={36} stroke={1.5} />
            </Dropzone.Idle>

            <Text size="sm">{t('catalog.import.dropzone')}</Text>
          </Group>
        </Dropzone>

        {pending && (
          <Group gap="xs">
            {COLLECTIONS.map(
              (collection) =>
                rowCounts[collection] !== undefined && (
                  <Badge key={collection} variant="light">
                    {labels.collection(collection)}: {rowCounts[collection]}
                  </Badge>
                ),
            )}
          </Group>
        )}

        <Group justify="flex-end">
          <Button variant="default" onClick={close}>
            {t('common.cancel')}
          </Button>
          <Button onClick={() => void submit()} loading={loading} disabled={!pending}>
            {t('catalog.import.submit')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function ImportReportView({ report }: { report: Report }) {
  const { t } = useTranslation();
  const labels = useLabels();

  return (
    <Stack gap="xs">
      {COLLECTIONS.map((collection) => {
        const collectionReport = report[collection];

        if (
          collectionReport.created === 0 &&
          collectionReport.updated === 0 &&
          collectionReport.errors.length === 0
        ) {
          return null;
        }

        return (
          <Alert
            key={collection}
            color={collectionReport.errors.length > 0 ? 'yellow' : 'teal'}
            title={labels.collection(collection)}
          >
            <Text size="sm">
              {t('catalog.import.summary', {
                created: collectionReport.created,
                updated: collectionReport.updated,
                failed: collectionReport.errors.length,
              })}
            </Text>
            {collectionReport.errors.length > 0 && (
              <List size="sm" mt="xs">
                {collectionReport.errors.map((rowError, index) => (
                  <List.Item key={index}>
                    {rowError.id
                      ? t('catalog.import.rowErrorWithId', {
                          index: rowError.index,
                          id: rowError.id,
                          reason: rowError.reason,
                        })
                      : t('catalog.import.rowError', {
                          index: rowError.index,
                          reason: rowError.reason,
                        })}
                  </List.Item>
                ))}
              </List>
            )}
          </Alert>
        );
      })}
    </Stack>
  );
}
